import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {PasswordValidators} from '../../helpers/password-validators';
import {AccountSharingService} from '../../services/account-sharing.service';
import {PasswordChange} from '../../models/password-change';
import { UserStore } from 'src/app/modules/authentication/state/user.store';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en'

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    standalone: false
})
export class ChangePasswordComponent implements OnInit {

  passwordForm: FormGroup;
  submitted = false;
  passwordStrength = {
    strength: 0,
    hasMinLength: false,
    hasUppercase: false,
    hasLowerCase: false,
    hasDigit: false,
    message: '',
    messageTitle: '',
    indiClass: ''
  };
  user: any;

  constructor(private fb: FormBuilder, private accountSharingService: AccountSharingService) {
  }

  ngOnInit(): void {
    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          PasswordValidators.patternValidator(new RegExp('(?=.*[0-9])'), {
            requiresDigit: true
          }),
          PasswordValidators.patternValidator(new RegExp('(?=.*[A-Z])'), {
            requiresUppercase: true
          }),
          PasswordValidators.patternValidator(new RegExp('(?=.*[a-z])'), {
            requiresLowercase: true
          })
        ])],
      confirmPassword: ['', Validators.required]
    }, {validators: PasswordValidators.MatchValidator});
    this.checkFormChanges();

    const options = {
      translations: zxcvbnEnPackage.translations,
      graphs: zxcvbnCommonPackage.adjacencyGraphs,
      dictionary: {
        ...zxcvbnCommonPackage.dictionary,
        ...zxcvbnEnPackage.dictionary,
      },
    }
    zxcvbnOptions.setOptions(options)

  }

  checkFormChanges(): void {
    this.passwordForm.get('password').valueChanges
      .subscribe({
        next: v => {
          if (!this.passwordForm.get('password')?.errors?.required) {               

            if (v?.length >= 8) {  
              
              this.passwordStrength.indiClass = 'is-error';

              if (!this.requiresLowercaseValid) {
                this.passwordStrength.messageTitle = 'Try again.';
                this.passwordStrength.message = 'Your password must contain at least one lowercase letter.';
              } else if (!this.requiresUppercaseValid) {
                this.passwordStrength.messageTitle = 'Try again.';
                this.passwordStrength.message = 'Your password must contain at least one uppercase letter.';
              } else if (!this.requiresDigitValid) {
                this.passwordStrength.messageTitle = 'Try again.';
                this.passwordStrength.message = 'Your password must contain at least one digital character.';

              // if basic requirements are met, check strength
              } else {

                const passwordStrengthScore = zxcvbn(this.passwordForm.get('password').value).score;
                this.passwordStrength.strength = passwordStrengthScore * 25; 

                switch (passwordStrengthScore) {
                  case 0: {
                    this.passwordStrength.indiClass = 'is-error';
                    this.passwordStrength.messageTitle = 'Easily guessable.';
                    this.passwordStrength.message = 'Please consider changing your password.';
                    break;
                  }
                  case 1: {
                    this.passwordStrength.indiClass = 'is-okay';
                    this.passwordStrength.messageTitle = 'That will do.';
                    this.passwordStrength.message = 'Your password is strong enough.';
                    break;
                  }
                  case 2: {
                    this.passwordStrength.indiClass = 'is-good';
                    this.passwordStrength.messageTitle = 'Nice.';
                    this.passwordStrength.message = 'Your password is strong enough.';
                    break;
                  }
                  case 3: {
                    this.passwordStrength.indiClass = 'is-good';
                    this.passwordStrength.messageTitle = 'Nice.';
                    this.passwordStrength.message = 'Your password is strong.';
                    break;
                  }
                  case 4: {
                    this.passwordStrength.indiClass = 'is-great';
                    this.passwordStrength.messageTitle = 'Great stuff!';
                    this.passwordStrength.message = 'Your password is very strong.';
                    break;
                  } 
                 
                }

            
              }
            
            // if not long enough
            } else {
              this.passwordStrength.indiClass = 'is-error is-short';
            }



          } else {
            this.passwordStrength = {
              strength: 0,
              hasMinLength: false,
              hasUppercase: false,
              hasLowerCase: false,
              hasDigit: false,
              message: '',
              messageTitle: '',
              indiClass: ''
            };
          }
        }
      });
    this.passwordForm.valueChanges.subscribe({
      next: () => {
        if (this.passwordForm.valid) {
          const passwordChange = new PasswordChange();
          passwordChange.currentPassword = this.passwordForm.get('currentPassword')?.value;
          passwordChange.newPassword = this.passwordForm.get('password')?.value;
          this.accountSharingService.updateChangePassword(passwordChange);
        } else {
          this.accountSharingService.updateChangePassword(null);
        }
      }
    });
  }

  get minLengthValid(): boolean {
    return !this.passwordForm?.controls?.password.hasError('minlength');
  }

  get requiresDigitValid(): boolean {
    return !this.passwordForm?.controls?.password.hasError('requiresDigit');
  }

  get requiresUppercaseValid(): boolean {
    return !this.passwordForm?.controls?.password.hasError('requiresUppercase');
  }

  get requiresLowercaseValid(): boolean {
    return !this.passwordForm?.controls?.password.hasError('requiresLowercase');
  }
}
